/**
 * الحساب المعطَّل: أين يظهر وأين لا يظهر — قاعدة واحدة في مكان واحد.
 *
 * التعطيل في هذا النظام **ليس حذفًا**: الصفّ يبقى، وكل ما ارتبط به يبقى (حجوزات، دفعات،
 * حضور، تسويات، تدقيق)، والدخول وحده يُمنع (`auth` يفحص `is_active`) والجلسات تُلغى.
 * ولهذا لا يجوز إخفاؤه من كل مكان: تقرير الشهر الماضي لا يصير صحيحًا بحذف اسم صاحبه.
 *
 * فالقاعدة في اتجاهين:
 *   • **قوائم العمل الجاري** (إسناد جديد، إصدار جدول، لوحة اليوم، تسجيل حضور): النشِط
 *     وحده — من لا يستطيع الدخول لا يُسنَد إليه عمل اليوم. استعمل `activeOnly`.
 *   • **قوائم التاريخ والتقارير** (تصدير، مراجعة مدفوعات، أيام موظف مضت): يظهر، لكن
 *     **موسومًا** حتى لا يُختار على أنه حساب عامل. استعمل `nameWithState`.
 *
 * وشاشة إدارة الموظفين حالة ثالثة: تُخفيهم افتراضيًا (فهي القائمة «العادية») مع مفتاح
 * يُظهرهم — وإلا صار الحساب المعطَّل غير قابل لإعادة التفعيل أصلًا.
 */

/** من يصلح لعملٍ جديد: النشِط وحده. */
export const activeOnly = <T extends { isActive: boolean }>(people: T[]): T[] =>
  people.filter(p => p.isActive);

/** الاسم موسومًا بحاله — في القوائم التي تُظهر المعطَّلين لغرض تاريخي. */
export const nameWithState = (p: { fullNameAr: string; isActive: boolean }): string =>
  (p.isActive ? p.fullNameAr : `${p.fullNameAr} (معطَّل)`);

/**
 * من يُنتقى في قوائم «البياع» — أي **من يسجّل عمليات**: البياعون والشركاء **ومعهم
 * المالك**، فهو يسجّل حجوزات بنفسه (قراره: اسمه مع البياعين، والرقم واحد).
 *
 * وهي غير قوائم «الموظفين» الإدارية (الملف الوظيفي، إسناد الجداول): تلك للموظفين
 * وحدهم. وحسابات الفحص خارج الاثنتين — يُسقطها الخادم قبل أن تصل (Migration 0045).
 */
/**
 * ترتيب صفوف اللوحات: **الإدارة أولًا** (المالك والأدمن ثم الشركاء) ثم البياعون — وداخل كل
 * فئة بالأقدمية، والرقم الوظيفي هو درجتها منذ 0053. ومن لا رقم له يقع في آخر فئته.
 */
export function byManagementThenSeniority(
  a: { role: 'ADMIN' | 'SALES' | 'PARTNER'; employeeNo?: string | null; fullNameAr: string },
  b: { role: 'ADMIN' | 'SALES' | 'PARTNER'; employeeNo?: string | null; fullNameAr: string },
): number {
  const rank = (r: string) => (r === 'ADMIN' ? 0 : r === 'PARTNER' ? 1 : 2);
  if (rank(a.role) !== rank(b.role)) return rank(a.role) - rank(b.role);
  const no = (v?: string | null) => (v ? Number(v.replace(/\D/g, '')) : Number.MAX_SAFE_INTEGER);
  if (no(a.employeeNo) !== no(b.employeeNo)) return no(a.employeeNo) - no(b.employeeNo);
  return a.fullNameAr.localeCompare(b.fullNameAr, 'ar');
}

export const bookingActors = <T extends { role: 'ADMIN' | 'SALES' | 'PARTNER'; isOwner: boolean }>(people: T[]): T[] =>
  people.filter(p => p.role === 'SALES' || p.role === 'PARTNER' || p.isOwner);
